import { InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { IlsSearchSummary, NsnManufacturer, NsnReport } from '@/api/generatedApi';

const SummaryText = styled(Box)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
  marginTop: '8px',
  cursor: 'default',
}));

const InfoButton = styled(IconButton)(() => ({
  padding: '2px',
  marginLeft: '2px',
  verticalAlign: 'text-bottom',
  color: 'inherit',
}));

const InfoIcon = styled(InfoOutlined)(() => ({
  fontSize: '14px',
}));

const MAX_LISTED_ALTERNATES = 10;

export const MCRL_TOOLTIP =
  "ILS looks up the searched number in the government's Master Cross Reference List (MCRL) and returns stock listed under any part number cross-referenced to it. These alternates were covered by that one search, with no separate ILS calls.";

const normalize = (value: string): string =>
  [...value]
    .filter(character => /[a-z0-9]/i.test(character))
    .join('')
    .toUpperCase();

export const coveredAlternates = (term: string, manufacturers: NsnManufacturer[] = []): string[] => {
  const seen = new Set([normalize(term)]);
  const alternates: string[] = [];
  for (const { partNumber } of manufacturers) {
    const key = normalize(partNumber);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    alternates.push(partNumber);
  }
  return alternates;
};

export const listAlternates = (alternates: string[]): string => {
  const listed = alternates.slice(0, MAX_LISTED_ALTERNATES).join(', ');
  const remaining = alternates.length - MAX_LISTED_ALTERNATES;
  return remaining > 0 ? `${listed} and ${remaining} more` : listed;
};

const alternatesCoveredBy = (summary: IlsSearchSummary, nsnReport?: NsnReport): string[] => {
  const searchedTheNsn = nsnReport !== undefined && normalize(summary.term) === normalize(nsnReport.nsn);
  return !summary.failed && searchedTheNsn ? coveredAlternates(summary.term, nsnReport.manufacturers) : [];
};

export const describeIlsSearch = (summary: IlsSearchSummary): string => {
  if (summary.failed) {
    return `ILS search on ${summary.term} failed`;
  }
  const suppliers = `${summary.supplierCount} ${summary.supplierCount === 1 ? 'supplier' : 'suppliers'}`;
  return `ILS searched ${summary.term} · ${suppliers}`;
};

interface IlsSearchSummaryLineProperties {
  summary?: IlsSearchSummary;
  nsnReport?: NsnReport;
}

const IlsSearchSummaryText = ({
  summary,
  nsnReport,
}: IlsSearchSummaryLineProperties & { summary: IlsSearchSummary }) => {
  const alternates = alternatesCoveredBy(summary, nsnReport);
  return (
    <SummaryText data-testid='ils-search-summary'>
      {describeIlsSearch(summary)}
      {alternates.length > 0 && (
        <span data-testid='ils-covered-alternates'>
          . Also covers alternate part numbers {listAlternates(alternates)}
          <Tooltip title={MCRL_TOOLTIP} describeChild>
            <InfoButton size='small' aria-label='How ILS covers alternate part numbers'>
              <InfoIcon />
            </InfoButton>
          </Tooltip>
        </span>
      )}
    </SummaryText>
  );
};

export const IlsSearchSummaryLine = ({ summary, nsnReport }: IlsSearchSummaryLineProperties) =>
  summary && <IlsSearchSummaryText summary={summary} nsnReport={nsnReport} />;
